import { StatusDot } from "./StatusDot";
import type { AccountStatus } from "../lib/types";

export interface ArchiveRowData {
  render_id: string;
  draft_id: string;
  display_name: string | null;
  cadence: "weekly" | "monthly";
  period_start: string;
  period_end: string;
  status: string;             // draft.status
  delivery_status: string;    // render.delivery_status
  rendered_at: string;
}

interface Props {
  row: ArchiveRowData;
  onRetry?: (draftId: string) => void;
}

// Map draft + delivery status to a single user-facing label.
function statusFor(row: ArchiveRowData): { label: string; dot: AccountStatus } {
  if (row.delivery_status === "bounced" || row.delivery_status === "complaint" || row.delivery_status === "failed") {
    return { label: "failed", dot: "flagged" };
  }
  if (row.status === "failed") return { label: "failed", dot: "flagged" };
  if (row.delivery_status === "delivered" || row.status === "sent") return { label: "sent", dot: "healthy" };
  if (row.status === "rendering" || row.status === "approved") return { label: "pending", dot: "warning" };
  return { label: row.status, dot: "idle" };
}

function fmtPeriod(row: ArchiveRowData): string {
  return row.cadence === "weekly"
    ? `${row.period_start} – ${row.period_end}`
    : new Date(row.period_start + "T00:00:00Z").toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

export function ArchiveRow({ row, onRetry }: Props) {
  const status = statusFor(row);
  const isFailed = status.label === "failed";

  return (
    <div className="archive-row" style={{
      display: "grid",
      gridTemplateColumns: "minmax(140px, 1.5fr) 100px 1fr 100px 160px",
      gap: 12, padding: "12px 10px", minHeight: 44, alignItems: "center",
      borderBottom: "1px solid rgba(255,255,255,0.06)", fontSize: "var(--fs-sm)",
    }}>
      <div>
        <StatusDot status={status.dot} />
        {row.display_name ?? "(unknown)"}
      </div>
      <span style={{ fontSize: "var(--fs-xs)", color: "var(--color-bone-dim)", textTransform: "uppercase", letterSpacing: "var(--letter-label)" }}>
        {row.cadence}
      </span>
      <span style={{ fontFamily: "var(--font-mono)" }}>{fmtPeriod(row)}</span>
      <span style={{ color: status.dot === "flagged" ? "#d9645c" : "var(--color-bone-dim)" }}>
        {status.label}
      </span>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        {!isFailed && (
          <>
            <a
              href={`/api/archive/render/${row.render_id}/pdf`}
              download
              style={{ fontSize: "var(--fs-xs)", padding: "4px 10px", background: "rgba(255,255,255,0.06)", color: "var(--color-bone)", textDecoration: "none", borderRadius: 4 }}
            >PDF</a>
            <a
              href={`/api/archive/render/${row.render_id}/html`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: "var(--fs-xs)", padding: "4px 10px", background: "rgba(255,255,255,0.06)", color: "var(--color-bone-dim)", textDecoration: "none", borderRadius: 4 }}
            >Preview</a>
          </>
        )}
        {isFailed && onRetry && (
          <button
            type="button"
            onClick={() => onRetry(row.draft_id)}
            style={{
              fontSize: "var(--fs-xs)", padding: "4px 10px", border: "none", cursor: "pointer",
              background: "rgba(217,100,92,0.2)", color: "#ffc8c4", borderRadius: 4,
            }}
          >Retry send</button>
        )}
      </div>
    </div>
  );
}
